import {
    CAGE_SCOPED_QUERY_PARAMS,
    QUERY_SELECTED_FISH_GROUP_ID,
} from '@/contexts/cageFilter/params';
import { OVERVIEW_CAGE_STATUSES } from '@/domain/client';
import { createCagePageUrl } from '@/utils/urlGenerator';

import type { BreadcrumbCage, CagesByStatus } from './types';

/**
 * Date-range params that stay on a same-sub-page step. The pen menu leaves them
 * behind on purpose, because a range explored on one page should not follow a
 * jump to a different page. Previous and next stay on the page the reader is
 * already on, so the range they picked stays with them.
 *
 * `fg` is not a date param and is not copied: the destination entry supplies it.
 */
const DATE_RANGE_PARAM = /^(?:date-from|date-to|from|to|.+-from|.+-to)$/;
const CAGE_VIEW_PARAMS: readonly string[] = CAGE_SCOPED_QUERY_PARAMS.filter(
    (key) => key !== QUERY_SELECTED_FISH_GROUP_ID
);

export interface PenStepNeighbors {
    previous: BreadcrumbCage | null;
    next: BreadcrumbCage | null;
}

/**
 * The breadcrumb pen menu's clickable order: status buckets, then name within each.
 * With fish groups on, each entry is that pen's group; with it off, entries are pens.
 */
export function orderedBreadcrumbCages(cagesByStatus: CagesByStatus): BreadcrumbCage[] {
    return OVERVIEW_CAGE_STATUSES.flatMap((status) => cagesByStatus[status]);
}

/**
 * The current item is the pen the reader is on, the same match the menu uses
 * (`cage.id === selectedCageId`). Missing from the list means both ends are closed.
 */
export function penStepNeighbors(
    cages: readonly BreadcrumbCage[],
    currentCageId: number | undefined
): PenStepNeighbors {
    const index = currentCageId == null ? -1 : cages.findIndex((cage) => cage.id === currentCageId);

    return {
        previous: index > 0 ? cages[index - 1] : null,
        next: index >= 0 && index < cages.length - 1 ? cages[index + 1] : null,
    };
}

/**
 * The pen's name, followed by the cohort in it when there is one to name.
 *
 * No feature-flag check: `fishGroupName` is only ever populated when the overview
 * was asked for cohort data, so with the flag off every entry renders the bare name.
 */
export function breadcrumbCageLabel(cage: BreadcrumbCage): string {
    return cage.fishGroupName ? `${cage.name} - ${cage.fishGroupName}` : cage.name;
}

/**
 * The destination the pen menu would open: same sub-page, the destination pen's own
 * fish group when the list carries one, and the date range the reader already had.
 */
export function penStepHref(input: {
    clientId: number;
    locationId: number;
    cage: BreadcrumbCage;
    pagePath: string;
    search: string;
    tracksFishGroups: boolean;
}): string {
    const page = input.pagePath || '/dashboard';
    const fishGroupId = input.tracksFishGroups ? input.cage.fishGroupId : undefined;
    const base = createCagePageUrl(
        input.clientId,
        input.locationId,
        input.cage.id,
        page,
        fishGroupId
    );
    const [path, baseQuery = ''] = base.split('?');
    const carried = new URLSearchParams(baseQuery);
    const current = new URLSearchParams(input.search);

    for (const [key, value] of current.entries()) {
        const keep = DATE_RANGE_PARAM.test(key) || CAGE_VIEW_PARAMS.includes(key);
        if (keep && value !== '') {
            carried.set(key, value);
        }
    }

    const serialised = carried.toString();
    return serialised === '' ? path : `${path}?${serialised}`;
}
